import { useCallback, useEffect, useState } from 'react';
import {
  ArrowRight, Check, CircleAlert, ExternalLink, Loader2, PackageOpen, Play, RotateCw, ScanSearch, Search, ShieldCheck, Square,
} from 'lucide-react';
import { useNavigation } from '../../context/NavigationContext';
import { apiError, getCatalogueBatches, importDesigns, listDomains, listScrapes } from '../../api/catalogue';
import { cldThumb } from '../../utils/img';
import FlowChart from './FlowChart';
import {
  CATALOGUE_CSS, Legend, Notice, PageHeader, PrimaryButton, RESULTS, RUN_END, STATUS, SecondaryButton,
  SegmentBar, ago, elapsed, focusRing, num, useScrapeRun,
  isCopyJob,
} from './shared';

// Catalogue -> Pull & scrape. Step 1 copies designs (with the product link
// found in their image slots) into the catalogue; step 2 reads those pages in
// a background run. What came back is in Catalogue -> Full catalogue.

function StepBadge({ state }) {
  const base = 'h-7 w-7 rounded-full flex items-center justify-center shrink-0';
  if (state === 'done') return <span className={`${base} bg-zoddle-green text-white`}><Check size={15} strokeWidth={3} /></span>;
  if (state === 'running') return <span className={`${base} bg-sky-50 ring-2 ring-zoddle-blue text-zoddle-blue`}><Loader2 size={15} className="motion-safe:animate-spin" /></span>;
  if (state === 'waiting') return <span className={`${base} ring-2 ring-gray-200`}><span className="h-2 w-2 rounded-full bg-gray-300" /></span>;
  return <span className={`${base} ring-2 ring-zoddle-green`}><span className="h-2.5 w-2.5 rounded-full bg-zoddle-green" /></span>;
}

const STATE_WORD = { done: 'Done', running: 'Running', active: 'Ready', waiting: 'Waiting' };

function StepCard({ number, state, icon: Icon, title, hint, children, className = '' }) {
  const frame = state === 'running'
    ? 'border-sky-200 shadow-[0_0_0_4px_rgba(56,182,255,0.10)]'
    : 'border-gray-200 shadow-sm';
  return (
    <section aria-label={`Step ${number}: ${title}`} className={`rounded-xl border bg-white p-5 flex flex-col min-w-0 ${frame} ${className}`}>
      <div className="flex items-start gap-3">
        <StepBadge state={state} />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            Step {number} <span className="mx-1 text-gray-300">·</span>
            <span className={state === 'running' ? 'text-zoddle-blue' : state === 'done' ? 'text-zoddle-green' : ''}>{STATE_WORD[state]}</span>
          </p>
          <h3 className="mt-0.5 text-[15px] font-semibold leading-snug text-gray-900">{title}</h3>
          {hint && <p className="mt-1 text-[12.5px] leading-snug text-gray-500">{hint}</p>}
        </div>
        {Icon && <Icon size={18} aria-hidden className="shrink-0 mt-0.5 text-gray-400" />}
      </div>
      <div className="mt-4 flex-1 flex flex-col">{children}</div>
    </section>
  );
}

// The headline numbers above the flow; the ones that match a status open the
// Full catalogue on it.
function Stats({ designs, imported, counts, onPick }) {
  const c = counts || {};
  const withLink = ['pending', ...RESULTS].reduce((s, k) => s + (c[k] || 0), 0);
  const items = [
    { label: 'Designs in ZOCS', value: designs, swatch: 'bg-sky-400' },
    { label: 'In catalogue queue', value: imported, swatch: 'bg-sky-400', open: 'all' },
    { label: 'Have a product link', value: withLink, swatch: 'bg-sky-400' },
    { label: 'Scraped', value: c.done, swatch: 'bg-zoddle-green', open: 'done' },
    { label: 'Waiting', value: c.pending, swatch: 'bg-sky-200', open: 'pending' },
    { label: 'Couldn’t scrape', value: (c.failed || 0) + (c.dead_link || 0) + (c.unsupported || 0), swatch: 'bg-zoddle-pink' },
  ];
  return (
    <dl className="mt-6 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-px rounded-xl border border-gray-200 bg-gray-200 overflow-hidden">
      {items.map((it) => {
        const body = (
          <>
            <dt className="flex items-center gap-2 text-[12.5px] text-gray-500">
              {it.label}<span className={`h-0.5 w-5 rounded-full ${it.swatch}`} aria-hidden />
            </dt>
            <dd className="mt-1.5 text-[26px] leading-none font-semibold tracking-tight text-gray-900 tabular-nums">{num(it.value)}</dd>
          </>
        );
        return it.open
          ? <button key={it.label} onClick={() => onPick(it.open === 'all' ? null : it.open)}
              className={`text-left bg-white px-5 py-4 hover:bg-gray-50 transition-colors ${focusRing} focus-visible:ring-inset`}>{body}</button>
          : <div key={it.label} className="bg-white px-5 py-4">{body}</div>;
      })}
    </dl>
  );
}

function PullStep({ data, running, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const total = data?.designs_total ?? 0;
  const imported = data?.imported_total ?? 0;
  const remaining = Math.max(0, total - imported);

  async function pull() {
    setBusy(true); setError(''); setResult(null);
    try {
      setResult(await importDesigns({ all: true }));
      onDone();
    } catch (e) {
      setError(apiError(e, 'Could not pull the designs in'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <StepCard number={1} state={data && total > 0 && remaining === 0 ? 'done' : 'active'} icon={PackageOpen}
      title="Pull designs in"
      hint="Copies every design from inventory that isn’t in the catalogue yet, with the product link found in its image slots. Designs already in are skipped."
      className="xl:flex-[1.25]">
      <p className="text-[13px] text-gray-500">
        <span className="text-[26px] leading-none font-semibold tracking-tight text-gray-900 tabular-nums">{num(imported)}</span>
        <span className="ml-1.5">of {num(total)} designs in</span>
      </p>
      <div className="mt-2.5 h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full bg-zoddle-green transition-[width] duration-500 ease-out" style={{ width: total ? `${(imported / total) * 100}%` : 0 }} />
      </div>
      <div className="pt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <PrimaryButton onClick={pull} disabled={running || busy || !data || remaining === 0}>
          {busy ? <Loader2 size={15} className="motion-safe:animate-spin" /> : <ArrowRight size={15} />}
          {remaining ? `Pull ${num(remaining)} new design${remaining === 1 ? '' : 's'} from inventory` : 'Everything is in'}
        </PrimaryButton>
        {running && <span className="text-[12px] text-gray-400">Waiting for the running job to finish</span>}
      </div>
      {result && (
        <Notice tone="green" icon={Check} className="mt-3">
          {result.added
            ? <>Pulled in <b>{num(result.added)}</b> designs — {num(result.with_link)} with a product link, {num(result.no_link)} without.</>
            : <>Nothing new — every design is already in.</>}
        </Notice>
      )}
      {error && <p className="mt-3 text-[12.5px] text-zoddle-pink">{error}</p>}
    </StepCard>
  );
}

// The designs behind a site's "N left": the ones still to scrape, with the link each one has.
const LEFT = 'pending,failed,dead_link,unsupported';
function SiteLinks({ site, refreshKey }) {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [more, setMore] = useState(false);
  const load = useCallback((offset) => {
    setMore(true);
    listScrapes({ domain: site.domain, status: LEFT, offset, limit: 20 })
      .then((d) => { setTotal(d.total); setRows((r) => (offset ? [...(r || []), ...d.rows] : d.rows)); })
      .catch(() => setRows((r) => r || []))
      .finally(() => setMore(false));
  }, [site.domain]);
  useEffect(() => { setRows(null); load(0); }, [load, refreshKey]);
  if (!rows) return <p className="mt-3 text-[12px] text-gray-400">Loading links…</p>;
  if (!rows.length) return <p className="mt-3 text-[12px] text-gray-400">Nothing left to scrape on {site.domain}.</p>;
  return (
    <div className="mt-3">
      <p className="text-[12px] font-medium text-gray-500">{num(total)} link{total === 1 ? '' : 's'} left to scrape on {site.domain}</p>
      <ul className="mt-1.5 rounded-lg border border-gray-200 divide-y divide-gray-100 max-h-72 overflow-y-auto">
        {rows.map((r) => (
          <li key={r.design_id} className="flex items-start gap-3 px-3 py-2">
            {r.image_url_1
              ? <img src={cldThumb(r.image_url_1, 80)} alt="" className="h-10 w-10 rounded object-cover bg-gray-100 shrink-0" />
              : <span className="h-10 w-10 rounded bg-gray-100 shrink-0" aria-hidden />}
            <div className="min-w-0 flex-1">
              <p className="text-[13px] text-gray-800 truncate">
                <span className="tabular-nums text-gray-500">{r.design_code}</span> · {r.design_name}{r.color ? ` · ${r.color}` : ''}
                <span className="text-gray-400"> · {r.brand}</span>
              </p>
              <a href={r.product_link} target="_blank" rel="noreferrer" title={r.product_link}
                className="inline-flex max-w-full items-center gap-1 text-[12px] text-sky-700 hover:underline">
                <span className="truncate">{r.product_link}</span><ExternalLink size={11} className="shrink-0" />
              </a>
              {r.last_error && <p className="text-[11.5px] text-gray-400 truncate" title={r.last_error}>{r.last_error}</p>}
            </div>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] ring-1 ${STATUS[r.status]?.pill}`}>{STATUS[r.status]?.label || r.status}</span>
          </li>
        ))}
      </ul>
      {rows.length < total && (
        <button onClick={() => load(rows.length)} disabled={more} className={`mt-1.5 text-[12px] text-sky-700 hover:underline disabled:opacity-50 ${focusRing}`}>
          Show {Math.min(20, total - rows.length)} more
        </button>
      )}
    </div>
  );
}

// Scrape one site: all its links (e.g. after a new parser) or only the ones not scraped yet.
function BySite({ busy, start, refreshKey }) {
  const [sites, setSites] = useState(null);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(null);
  useEffect(() => { listDomains().then(setSites).catch(() => setSites([])); }, [refreshKey]);
  const all = sites || [];
  const shown = all
    .filter((d) => !q || d.domain.includes(q.trim().toLowerCase()))
    .sort((x, y) => (y.remaining > 0) - (x.remaining > 0) || y.total - x.total || x.domain.localeCompare(y.domain));
  const site = all.find((d) => d.domain === picked);
  if (!sites || !all.length) return null;
  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <p className="text-[12px] font-semibold uppercase tracking-wider text-gray-400">By site</p>
      <div className="mt-2 rounded-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-2 px-3 h-9 border-b border-gray-100 bg-gray-50/60">
          <Search size={14} className="text-gray-400 shrink-0" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${all.length} sites`}
            aria-label="Find a site" className="flex-1 min-w-0 text-[13px] bg-transparent outline-none placeholder:text-gray-400" />
        </div>
        <ul className="max-h-52 overflow-y-auto py-1" role="listbox" aria-label="Sites">
          {shown.length === 0 && <li className="px-3 py-3 text-[12.5px] text-gray-400">No site called “{q.trim()}” in the catalogue.</li>}
          {shown.map((d) => {
            const on = d.domain === picked;
            return (
              <li key={d.domain} role="option" aria-selected={on}>
                <button onClick={() => setPicked(on ? null : d.domain)}
                  className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left ${on ? 'bg-sky-50' : 'hover:bg-gray-50'} ${focusRing} focus-visible:ring-inset`}>
                  <span className={`h-3.5 w-3.5 rounded-full border shrink-0 ${on ? 'border-sky-500 bg-sky-500 ring-2 ring-sky-100' : 'border-gray-300'}`} aria-hidden />
                  <span className="flex-1 min-w-0 truncate text-[13px] text-gray-800">{d.domain}</span>
                  {!d.parser && <span className="text-[11px] text-gray-400 whitespace-nowrap" title="No parser of its own: read with the general page reader">general reader</span>}
                  <span className="text-[11.5px] text-gray-400 tabular-nums whitespace-nowrap">{num(d.total - d.remaining)} of {num(d.total)} scraped</span>
                  <span className={`w-20 text-right text-[11.5px] tabular-nums whitespace-nowrap ${d.remaining ? 'text-sky-700 font-medium' : 'text-gray-400'}`}>
                    {d.remaining ? `${num(d.remaining)} left` : 'all done'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      {site && site.remaining > 0 && <SiteLinks site={site} refreshKey={refreshKey} />}
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <SecondaryButton onClick={() => start('domain_all', [], { domain: picked })} disabled={busy || !site}>
          <RotateCw size={14} /> {site ? `Scrape all ${num(site.total)} on ${site.domain}` : 'Scrape all'}
        </SecondaryButton>
        <SecondaryButton onClick={() => start('domain_remaining', [], { domain: picked })} disabled={busy || !site || !site.remaining}>
          <Play size={13} className="fill-current" /> {site ? `Scrape the remaining ${num(site.remaining)}` : 'Scrape the remaining'}
        </SecondaryButton>
      </div>
      {site && (
        <p className="mt-1.5 text-[12px] text-gray-400">
          {Object.entries(site.counts).map(([k, n]) => `${num(n)} ${(STATUS[k]?.label || k).toLowerCase()}`).join(' · ')}
        </p>
      )}
    </div>
  );
}

function ScrapeStep({ counts, retryable, decodoReady, scrape, hasDesigns, onPick }) {
  const { run, running, busy, error, start, stop } = scrape;
  const pending = counts?.pending ?? 0;
  const failed = counts?.failed ?? 0;
  const withLink = ['pending', ...RESULTS].reduce((s, k) => s + (counts?.[k] ?? 0), 0);
  const state = running ? 'running' : !hasDesigns ? 'waiting' : pending > 0 ? 'active' : withLink > 0 ? 'done' : 'waiting';

  return (
    <StepCard number={2} state={state} icon={ScanSearch} title="Scrape product pages"
      hint={hasDesigns
        ? 'Reads each product link for the title, description, photos and sizes. Runs in the background — you can leave this page.'
        : 'Pull designs in first.'}
      className="xl:flex-[1.25]">
      {running ? (
        <>
          <p className="text-[13px] text-gray-500">
            <span className="text-[26px] leading-none font-semibold tracking-tight text-gray-900 tabular-nums">{num(run.total)}</span>
            <span className="ml-1.5">{isCopyJob(run) ? 'designs getting their page photos copied' : 'links sent for scraping'}</span>
            <span className="float-right text-[12px] text-gray-400 tabular-nums">{elapsed(run.started_at)}</span>
          </p>
          <div className="mt-2.5 h-2 rounded-full bg-sky-100 overflow-hidden" aria-hidden>
            <div className="h-full w-full catalogue-stream" />
          </div>
          <p className="mt-2.5 text-[12px] text-gray-500">
            {isCopyJob(run)
              ? 'A photo copy from the Images page is running; scraping can start once it finishes.'
              : 'Every site is being read at once. Results fill in on the chart above as pages come back.'}
          </p>
          <div className="pt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
            <SecondaryButton tone="pink" onClick={stop} disabled={busy || run.stop_requested}>
              <Square size={12} className="fill-current" />
              {run.stop_requested ? 'Stopping…' : 'Stop'}
            </SecondaryButton>
            <span className="text-[12px] text-gray-400">Started by {run.started_by}</span>
          </div>
        </>
      ) : (
        <>
          <p className="text-[13px] text-gray-500">
            <span className="text-[26px] leading-none font-semibold tracking-tight text-gray-900 tabular-nums">{num(pending)}</span>
            <span className="ml-1.5">waiting to be scraped</span>
          </p>
          <SegmentBar className="mt-2.5" total={withLink} counts={counts} />
          <Legend counts={counts} onPick={onPick} />
          <div className="pt-4 flex flex-wrap items-center gap-2">
            <PrimaryButton onClick={() => start('pending')} disabled={busy || pending === 0}>
              {busy ? <Loader2 size={15} className="motion-safe:animate-spin" /> : <Play size={13} className="fill-current" />}
              {pending ? `Scrape ${num(pending)} designs` : 'Nothing waiting'}
            </PrimaryButton>
            {retryable > 0 && (
              <SecondaryButton onClick={() => start('retry_failed')} disabled={busy}>
                <RotateCw size={14} /> Retry {num(retryable)} failed
              </SecondaryButton>
            )}
            {failed > 0 && (
              <SecondaryButton onClick={() => start('decodo_failed')} disabled={busy || !decodoReady}
                title={decodoReady ? 'Read every failed page again through Decodo (paid, slower, gets past sites that block us)'
                  : 'Decodo is not set up on this server (DECODO_TOKEN is missing)'}>
                <ShieldCheck size={14} /> Scrape {num(failed)} failed with Decodo
              </SecondaryButton>
            )}
          </div>
          {failed > 0 && !decodoReady && (
            <p className="mt-2 text-[12px] text-gray-400">Decodo isn’t set up on this server, so failed pages can’t be sent through it.</p>
          )}
          <BySite busy={busy} start={start} refreshKey={run?.finished_at} />
          {run && RUN_END[run.status] && (
            <p className="mt-3 text-[12px] text-gray-400">
              Last {isCopyJob(run) ? 'photo copy' : 'run'} {RUN_END[run.status]} {ago(run.finished_at)} · {num(run.processed)} of {num(run.total)} {isCopyJob(run) ? 'designs' : 'read'}
              {run.error && run.status === 'failed' && <span className="block mt-0.5 text-zoddle-pink">{run.error}</span>}
            </p>
          )}
        </>
      )}
      {error && <p className="mt-3 text-[12.5px] text-zoddle-pink">{error}</p>}
    </StepCard>
  );
}

export default function PullScrape() {
  const { navigate } = useNavigation();
  const [batches, setBatches] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState('');

  const loadBatches = useCallback(async () => {
    try { setBatches(await getCatalogueBatches()); } catch (e) { setLoadError(apiError(e, 'Could not load the catalogue')); }
  }, []);
  const loadSummary = useCallback(async () => {
    try { setSummary(await listScrapes({ limit: 1 })); } catch (e) { setLoadError(apiError(e, 'Could not load the catalogue')); }
  }, []);

  const scrape = useScrapeRun((what) => {
    loadSummary();
    if (what === 'finished') loadBatches();
  });

  useEffect(() => { loadBatches(); loadSummary(); }, [loadBatches, loadSummary]);

  const hasDesigns = (batches?.imported_total ?? 0) > 0;
  const refreshAll = () => { loadBatches(); loadSummary(); };
  // Full catalogue opens on Scraped by itself, so "everything" is asked for by name.
  const openCatalogue = (status) => navigate('cataloguedata', { status: status || 'all' });
  const designs = batches?.designs_total ?? 0;
  const imported = batches?.imported_total ?? 0;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1440px]">
      <style>{CATALOGUE_CSS}</style>
      <PageHeader title="Pull & scrape">
        Pull designs in from inbounding, then read each product page for its title, description, photos and sizes.
        Both steps can run again; what’s done is kept.
      </PageHeader>

      {loadError && <Notice icon={CircleAlert} className="mt-4">{loadError}</Notice>}

      <Stats designs={designs} imported={imported} counts={summary?.counts} onPick={openCatalogue} />

      <section aria-label="How designs flow" className="mt-4 rounded-xl border border-gray-200 bg-white px-5 pt-5 pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-[13px] font-semibold text-gray-900">How designs flow</h2>
          {scrape.running
            ? (
              <span className="inline-flex items-center gap-2 h-7 px-2.5 rounded-lg bg-sky-50 text-[12px] font-medium text-sky-700 ring-1 ring-inset ring-sky-200">
                <Loader2 size={13} className="motion-safe:animate-spin" />
                {isCopyJob(scrape.run) ? `Copying photos for ${num(scrape.run.total)} designs` : `Scraping ${num(scrape.run.total)} links`}
              </span>
            )
            : <span className="text-[12px] text-gray-400">Click a stage to see its designs</span>}
        </div>
        <div className="mt-2">
          <FlowChart designs={designs} imported={imported} counts={summary?.counts}
            running={scrape.running && !isCopyJob(scrape.run)} onPick={openCatalogue} />
        </div>
      </section>

      <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <PullStep data={batches} running={scrape.running} onDone={refreshAll} />
        <ScrapeStep counts={summary?.counts} retryable={summary?.retryable_failed ?? 0}
          decodoReady={!!summary?.decodo_ready} scrape={scrape}
          hasDesigns={hasDesigns} onPick={openCatalogue} />
      </div>
    </div>
  );
}
